// apps/web/src/features/crm/leads/card/LeadCardPage.tsx
//
// `/leads/:id` — the full-page, 3-zone v4 lead card (#2308, #2247 FR-2). The
// route `:id` IS the Lead UUID (BR-4); the header/summary reads the READ-ONLY
// Engagement projection via `useEngagementByLead` (fidelity variant, so the
// header carries title/source/phone/email/assigneeName), while the
// center/sidebar sub-resources + every mutation use the legacy lead surfaces
// keyed by that same id. The stage mutation is hoisted here so the header rail,
// side actions, and the phase-banner resume action share one flushed mutation.
// Layout is the `rich-entity-detail` tier: centered 1120px column, header card
// → phase banner → body grid (center + sidebar), single column when narrow.
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useEngagementByLead } from '@magimp/shared-hooks';
import { ErrorState, LoadingState, useNarrow } from '../../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../../stores/auth.js';
import { errorMessage } from '../../../../api/errors.js';
import { useAutoMarkLeadCallbackViewed } from '../useAutoMarkLeadCallbackViewed.js';
import { LeadCardHeader } from './LeadCardHeader.js';
import { LeadCardPhaseBanner } from './LeadCardPhaseBanner.js';
import { LeadCardManageActions } from './LeadCardManageActions.js';
import { LeadCardBody } from './LeadCardBody.js';
import { LeadCardSidebar } from './LeadCardSidebar.js';
import { useLeadStageActions } from './useLeadStageActions.js';

export function LeadCardPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['leads', 'common']);
  const perms = useAuthStore((s) => s.permissions);
  const userId = useAuthStore((s) => s.user?.id);
  const isAdmin = perms === 'ALL';
  const canManage = isAdmin || hasPermission(perms, 'leads.manage');
  const canAssign = isAdmin || hasPermission(perms, 'leads.assign');
  const canReadClients = isAdmin || hasPermission(perms, 'clients.read');
  const narrow = useNarrow(860);

  // WHY: preserves the episode page's side effect — publishes the active LEAD
  // notification target + fires mark-viewed-by-target on mount so lead_callback
  // notifications clear when the card opens. Called before early returns so the
  // hook order stays stable.
  useAutoMarkLeadCallbackViewed(id ?? null);

  // Hoisted so the header rail/side actions and the phase-banner resume share
  // one mutation instance (each flushes the projection on success). Safe with a
  // fallback id — the mutation only fires on user action, and the card returns
  // null before any control renders when `id` is missing.
  const stageActions = useLeadStageActions(id ?? '');

  const detailQ = useEngagementByLead(id ?? null, { fidelity: true });

  if (detailQ.isPending) return <LoadingState />;
  if (detailQ.error) {
    return (
      <ErrorState
        title={t('common:states.loadError')}
        text={errorMessage(detailQ.error)}
        onRetry={() => void detailQ.refetch()}
      />
    );
  }
  if (!detailQ.isSuccess || !id) return null;

  const detail = detailQ.data;

  // D-1 (#2318, SECURITY): mirrors the #2311 backend owner-only CP-Lead link
  // guard so the CP menu item never offers a create the backend would
  // 404. Admin links any lead; a non-admin manager links ONLY their own
  // (`detail.assigneeId` is the projected `Lead.managerId`, D-2308) — a pool
  // lead (`assigneeId === null`) always gates false.
  const canCreateProposal =
    isAdmin ||
    (hasPermission(perms, 'commercialProposals.manage') &&
      !!detail.assigneeId &&
      detail.assigneeId === userId);
  // #2317 (SECURITY): the single card-mutation editability gate — admin, or the
  // ASSIGNED manager with `leads.manage`. A pool lead (`assigneeId === null`)
  // gates false → a distributor assigns it before the recipient gets
  // owner-scoped edit controls.
  // Threaded to the header score edit, the checklist toggle, and the task
  // cancel/reopen controls; a pool / non-owner viewer sees none of them
  // (static read-only).
  const canEditCard =
    isAdmin ||
    (hasPermission(perms, 'leads.manage') && !!detail.assigneeId && detail.assigneeId === userId);
  // D-2 convenience seed for the CP create form — NOT authoritative (the
  // backend re-resolves the lead independently); bound client name first,
  // else the lead's own contact/display name already resolved here.
  const clientPrefill = detail.client?.name ?? detail.name ?? '';

  return (
    <div
      style={{
        maxWidth: 1120,
        margin: '0 auto',
        padding: '20px 24px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        width: '100%',
      }}
    >
      <LeadCardHeader
        leadId={id}
        detail={detail}
        canManage={canManage}
        canReadClients={canReadClients}
        canEditCard={canEditCard}
        onTransition={stageActions.run}
        busy={stageActions.isPending}
      />
      <LeadCardPhaseBanner
        detail={detail}
        canManage={canManage}
        onBackToWork={() => stageActions.run('in_work')}
      />
      <LeadCardManageActions leadId={id} canAssign={canAssign} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1.65fr) minmax(280px, 1fr)',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <LeadCardBody
          leadId={id}
          engagementId={detail.id}
          phase={detail.phase}
          canManage={canManage}
          canEditCard={canEditCard}
        />
        <LeadCardSidebar
          leadId={id}
          canManage={canManage}
          canCreateProposal={canCreateProposal}
          clientPrefill={clientPrefill}
        />
      </div>
    </div>
  );
}
